/**
 * Recorte de la foto antes de buscar (BUS-04, rediseño del 14-09-2026 con el
 * buscador por foto de lowes.com como referencia).
 *
 * La foto se muestra entera sobre fondo oscuro y encima va un marco que se
 * arrastra por el centro para moverlo y por los bordes y esquinas para
 * cambiarle el tamano. Lo de afuera del marco queda atenuado. No hay
 * libreria: son `pointer events`, que sirven igual para el mouse y el dedo, y
 * unas pocas cuentas.
 *
 * El recorte se guarda en FRACCIONES de la foto (0 a 1), no en pixeles de
 * pantalla: asi no depende de cuanto mide la caja, y si la ventana cambia de
 * tamano el marco sigue apuntando al mismo pedazo de la foto.
 *
 * Con el teclado: flechas para mover el marco, Shift + flechas para
 * agrandarlo o achicarlo. Es lo minimo para que se pueda usar sin mouse.
 */
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';

import type { Recorte } from '../../utilidades/foto-busqueda';

/** Lado minimo del marco, como fraccion de la foto: menos que eso no se ve nada. */
const MINIMO = 0.08;
/** Cuanto mueve o agranda una flecha del teclado. */
const PASO_TECLADO = 0.02;

/** Que parte del marco se agarro: el centro mueve, lo demas estira ese lado. */
type Agarre = 'centro' | 'n' | 's' | 'e' | 'o' | 'ne' | 'no' | 'se' | 'so';

const AGARRES_BORDE: readonly Agarre[] = ['n', 's', 'e', 'o', 'ne', 'no', 'se', 'so'];

export interface RecortadorFotoProps {
  src: string;
  ancho: number;
  alto: number;
  recorte: Recorte;
  onChange(recorte: Recorte): void;
}

/** Donde queda la foto dentro de la caja, con `object-fit: contain` a mano. */
interface Encaje {
  x: number;
  y: number;
  ancho: number;
  alto: number;
}

const limitar = (valor: number, minimo: number, maximo: number): number =>
  Math.min(maximo, Math.max(minimo, valor));

/** Aplica un desplazamiento (en fracciones) al recorte de partida segun el agarre. */
function aplicar(partida: Recorte, agarre: Agarre, dx: number, dy: number): Recorte {
  if (agarre === 'centro') {
    return {
      ...partida,
      x: limitar(partida.x + dx, 0, 1 - partida.ancho),
      y: limitar(partida.y + dy, 0, 1 - partida.alto),
    };
  }

  let izquierda = partida.x;
  let derecha = partida.x + partida.ancho;
  let arriba = partida.y;
  let abajo = partida.y + partida.alto;

  if (agarre.includes('o')) izquierda = limitar(izquierda + dx, 0, derecha - MINIMO);
  if (agarre.includes('e')) derecha = limitar(derecha + dx, izquierda + MINIMO, 1);
  if (agarre.includes('n')) arriba = limitar(arriba + dy, 0, abajo - MINIMO);
  if (agarre.includes('s')) abajo = limitar(abajo + dy, arriba + MINIMO, 1);

  return { x: izquierda, y: arriba, ancho: derecha - izquierda, alto: abajo - arriba };
}

export function RecortadorFoto({ src, ancho, alto, recorte, onChange }: RecortadorFotoProps) {
  const caja = useRef<HTMLDivElement>(null);
  const [encaje, setEncaje] = useState<Encaje | null>(null);
  /** Lo que se agarro y donde estaba el recorte y el puntero al agarrarlo. */
  const arrastre = useRef<{ agarre: Agarre; partida: Recorte; px: number; py: number } | null>(
    null,
  );

  // La caja cambia de tamano con la ventana; el encaje de la foto se recalcula
  // con ella para que el marco no se despegue de la imagen.
  useEffect(() => {
    const elemento = caja.current;
    if (elemento === null) return;

    const medir = (): void => {
      const escala = Math.min(elemento.clientWidth / ancho, elemento.clientHeight / alto);
      const anchoVisible = ancho * escala;
      const altoVisible = alto * escala;
      setEncaje({
        x: (elemento.clientWidth - anchoVisible) / 2,
        y: (elemento.clientHeight - altoVisible) / 2,
        ancho: anchoVisible,
        alto: altoVisible,
      });
    };
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(elemento);
    return () => observador.disconnect();
  }, [ancho, alto]);

  function agarrar(evento: PointerEvent<HTMLElement>, agarre: Agarre): void {
    // Sin esto el navegador arrastra la imagen de fondo en vez del marco.
    evento.preventDefault();
    evento.stopPropagation();
    evento.currentTarget.setPointerCapture(evento.pointerId);
    arrastre.current = { agarre, partida: recorte, px: evento.clientX, py: evento.clientY };
  }

  function arrastrar(evento: PointerEvent<HTMLElement>): void {
    const actual = arrastre.current;
    if (actual === null || encaje === null) return;

    const dx = (evento.clientX - actual.px) / encaje.ancho;
    const dy = (evento.clientY - actual.py) / encaje.alto;
    onChange(aplicar(actual.partida, actual.agarre, dx, dy));
  }

  function soltar(evento: PointerEvent<HTMLElement>): void {
    if (arrastre.current === null) return;
    arrastre.current = null;
    evento.currentTarget.releasePointerCapture(evento.pointerId);
  }

  function teclear(evento: KeyboardEvent<HTMLDivElement>): void {
    const direccion: Record<string, [number, number]> = {
      ArrowLeft: [-PASO_TECLADO, 0],
      ArrowRight: [PASO_TECLADO, 0],
      ArrowUp: [0, -PASO_TECLADO],
      ArrowDown: [0, PASO_TECLADO],
    };
    const salto = direccion[evento.key];
    if (salto === undefined) return;

    evento.preventDefault();
    const [dx, dy] = salto;
    // Con Shift se estira el lado derecho o el de abajo; sin Shift se mueve.
    onChange(evento.shiftKey ? aplicar(recorte, 'se', dx, dy) : aplicar(recorte, 'centro', dx, dy));
  }

  const marco =
    encaje === null
      ? null
      : {
          left: encaje.x + recorte.x * encaje.ancho,
          top: encaje.y + recorte.y * encaje.alto,
          width: recorte.ancho * encaje.ancho,
          height: recorte.alto * encaje.alto,
        };

  return (
    <div className="recorte" ref={caja}>
      <img src={src} alt="" draggable={false} />

      {marco !== null ? (
        <div
          className="recorte-marco"
          style={marco}
          role="application"
          aria-label="Marco de recorte. Muévelo con las flechas; con Shift cambia de tamaño."
          tabIndex={0}
          onPointerDown={(evento) => agarrar(evento, 'centro')}
          onPointerMove={arrastrar}
          onPointerUp={soltar}
          onPointerCancel={soltar}
          onKeyDown={teclear}
        >
          {/* Los agarres solo deciden QUE se agarro; mover y soltar suben
              por burbujeo al marco, que es quien lleva la cuenta. */}
          {AGARRES_BORDE.map((agarre) => (
            <span
              key={agarre}
              className={`recorte-agarre ${agarre}`}
              onPointerDown={(evento) => agarrar(evento, agarre)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
